import React from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { Header } from '../feature/Header';
import { Footer } from '../feature/Footer';

const LABEL_MAP: Record<string, string> = {
  'services': 'Services',
  'property-tax': 'Property Tax',
  'land-tax': 'Land Tax (VLT)',
  'water-charges': 'Water Charges',
  'water-sewerage': 'Water & Sewerage',
  'trade-license': 'Trade License',
  'advertisement-tax': 'Advertisement Tax',
  'building-permission': 'Building Permission',
  'birth-death': 'Birth & Death',
  'marriage-registration': 'Marriage Registration',
  'sewerage-connection': 'Sewerage Connection',
  'right-of-way': 'Right of Way',
  'third-party-verification': 'Third-Party Verification',
  'know-your-dues': 'Know Your Dues',
  'self-assessment': 'Self Assessment',
  'range-wise-dues': 'Range-Wise Dues',
  'revision-petition': 'Revision Petition',
  'general-revision-petition': 'General Revision Petition',
  'mutation': 'Mutation',
  'addition-alteration': 'Addition / Alteration',
  'demolition': 'Demolition',
  'tax-exemption': 'Tax Exemption',
  'vacancy-remission': 'Vacancy Remission',
  'download-proceedings': 'Download Proceedings',
  'application-status': 'Application Status',
  'tax-calculator': 'Tax Calculator',
  'new-connection': 'New Connection',
  'additional-connection': 'Additional Connection',
  'change-of-use': 'Change of Use',
  'closure-of-connection': 'Closure of Connection',
  'reconnection': 'Reconnection',
  'water-tariff': 'Water Tariff',
  'regularization': 'Regularization',
  'how-to-file': 'How to File',
  'new': 'New License',
  'renewal': 'Renewal',
  'closure': 'Closure',
  'download-certificate': 'Download Certificate',
  'apply': 'Apply',
  'register-birth': 'Register Birth',
  'register-death': 'Register Death',
  'reissue-certificate': 'Reissue Certificate',
  'new-registration': 'New Registration',
  'notice-download': 'Notice Download',
  'new-connection': 'New Connection',
  'change-closets': 'Change Closets / Closure',
  'verify': 'Verify',
  'file-new-application': 'File New Application',
  'bps': 'Building Penalisation Scheme',
  'lrs': 'Layout Regularisation Scheme',
  'municipal-assets': 'Municipal Assets',
};

function formatLabel(slug: string): string {
  return LABEL_MAP[slug] || slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export const ServiceLayout: React.FC = () => {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Build breadcrumb items: Home > Services > Category > Service
  // Category segments (e.g. property-tax) link back to /services since there's no category index page
  const breadcrumbs = pathSegments.map((segment, index) => {
    const fullPath = '/' + pathSegments.slice(0, index + 1).join('/');
    const label = formatLabel(segment);
    const isLast = index === pathSegments.length - 1;
    // For category-level segments (depth 2, e.g. /services/property-tax), link to /services
    const path = index === 1 ? '/services' : fullPath;
    return { label, path, isLast };
  });

  // Check if we're on the /services index page
  const isIndexPage = pathSegments.length <= 1;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Header />
      <main className="flex-1">
        {/* Breadcrumb - shown on sub-pages, not the main services listing */}
        {!isIndexPage && (
          <div className="bg-stone-100 dark:bg-gray-800 border-b border-stone-200 dark:border-gray-700">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
              <nav className="flex items-center flex-wrap gap-y-1 text-sm">
                <Link to="/" className="text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 transition-colors">
                  <i className="ri-home-4-line text-base"></i>
                </Link>
                {breadcrumbs.map((crumb) => (
                  <React.Fragment key={crumb.path}>
                    <i className="ri-arrow-right-s-line text-stone-400 dark:text-gray-500 mx-1.5"></i>
                    {crumb.isLast ? (
                      <span className="text-stone-600 dark:text-gray-300 font-medium">{crumb.label}</span>
                    ) : (
                      <Link
                        to={crumb.path}
                        className="text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 transition-colors"
                      >
                        {crumb.label}
                      </Link>
                    )}
                  </React.Fragment>
                ))}
              </nav>
            </div>
          </div>
        )}
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};
